import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Send, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SUPPORT_EMAIL, API } from "@/lib/site";

const EMPTY = { name: "", company: "", email: "", interest: "", message: "" };

const INTEREST_OPTIONS = [
  "Trade Off",
  "Construction software / bespoke development",
  "Website from £49/month",
];

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setForm((f) => ({ ...f, interest: e.detail }));
    };
    window.addEventListener("prefill-interest", handler);
    return () => window.removeEventListener("prefill-interest", handler);
  }, []);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thanks — your enquiry has been received. We'll be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please email us directly instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/40 focus:border-brand-blue transition";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <Reveal className="lg:col-span-5">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-brand-blue">
              Contact
            </p>
            <h2 className="mt-4 font-heading font-700 text-3xl sm:text-4xl lg:text-5xl tracking-tight text-brand-navy">
              Get in touch
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              If you would like support with one of our products, or want to
              discuss a specialist software requirement, send us a message and
              we'll get back to you.
            </p>

            <div className="mt-8 flex items-center gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue">
                <Mail className="h-6 w-6" strokeWidth={2} />
              </span>
              <div>
                <p className="text-sm text-slate-500">Prefer email?</p>
                <a
                  data-testid="contact-email-link"
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="font-heading font-600 text-brand-navy hover:text-brand-blue transition-colors"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </div>

            <div className="mt-5 flex items-center gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue">
                <Phone className="h-6 w-6" strokeWidth={2} />
              </span>
              <div>
                <p className="text-sm text-slate-500">Give us a call</p>
                <a
                  data-testid="contact-phone-link"
                  href="tel:+441917511933"
                  className="font-heading font-600 text-brand-navy hover:text-brand-blue transition-colors"
                >
                  +44 (0)191 751 1933
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="rounded-2xl border border-slate-200 bg-slate-50 p-6 md:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-slate-700 mb-1.5"
                  >
                    Name <span className="text-brand-blue">*</span>
                  </label>
                  <input
                    data-testid="contact-input-name"
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="company"
                    className="block text-sm font-medium text-slate-700 mb-1.5"
                  >
                    Company
                  </label>
                  <input
                    data-testid="contact-input-company"
                    id="company"
                    name="company"
                    type="text"
                    value={form.company}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Company name"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Email <span className="text-brand-blue">*</span>
                </label>
                <input
                  data-testid="contact-input-email"
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="you@company.com"
                  required
                />
              </div>

              <div className="mt-4">
                <label
                  htmlFor="interest"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  What are you interested in?
                </label>
                <select
                  data-testid="contact-input-interest"
                  id="interest"
                  name="interest"
                  value={form.interest}
                  onChange={handleChange}
                  className={`${inputClass} bg-white`}
                >
                  <option value="">Select an option (optional)</option>
                  {INTEREST_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-4">
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Message <span className="text-brand-blue">*</span>
                </label>
                <textarea
                  data-testid="contact-input-message"
                  id="message"
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={handleChange}
                  className={`${inputClass} resize-y`}
                  placeholder="Tell us a little about what you need..."
                  required
                />
              </div>

              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={submitting}
                className="mt-6 inline-flex items-center justify-center gap-2 bg-brand-navy text-white font-medium px-7 py-3.5 rounded-lg hover:bg-brand-navy-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send message
                    <Send className="h-4 w-4" strokeWidth={2} />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
